import { ProcessLanguageLevelDto, ProcessRequirementsSectionTypeDto } from '@/api/generated';
import {
  CreateProcessTranslation,
  createProcessTranslation,
} from '@/assets/locales/views/processes/createProcess/createProcess.translation.en.ts';
import {
  processDetailsTranslation,
  ProcessDetailsTranslation,
} from '@/assets/locales/views/processes/details/processDetails.translation.en.ts';
import {
  processListTranslation,
  ProcessListTranslation,
} from '@/assets/locales/views/processes/list/processList.translation.en.ts';

export type ProcessesTranslation = {
  createProcess: CreateProcessTranslation;
  details: ProcessDetailsTranslation;
  form: {
    buttons: {
      back: string;
      close: string;
      next: string;
      save: string;
    };
    cvSearch: {
      cancel: string;
      confirm: string;
      cvSearchTitle: string;
      dialogTitle: string;
      emptyTags: string;
      failed: string;
      finishingAnalysis: string;
      generatingHints: string;
      locationRegionMatched: string;
      locationRemote: string;
      locationUnrecognized: string;
      retry: string;
      skip: string;
      started: string;
      subtitle: string;
      unrecognizedLanguages: string;
    };
    languages: {
      addNewLanguage: string;
      cancel: string;
      deleteLanguage: string;
      dialogTitle: string;
      infotip: string;
      language: string;
      level: string;
      newLanguagePlaceholder: string;
      saveChanges: string;
      sectionTitle: string;
      subtitle: string;
      tabs: Record<ProcessLanguageLevelDto, string>;
      title: string;
    };
    redFlags: {
      addNewRedFlag: string;
      cancel: string;
      deleteRedFlag: string;
      dialogTitle: string;
      infotip: string;
      newRedFlagsPlaceholder: string;
      redFlag: string;
      saveChanges: string;
      sectionTitle: string;
      subtitle: string;
      title: string;
    };
    requirements: {
      actions: string;
      addNew: string;
      addNiceToHave: string;
      addRequired: string;
      cancel: string;
      deleteRequirement: string;
      dialogTitle: string;
      importance: string;
      importanceValue: string;
      infotip: string;
      moveToNiceToHave: string;
      moveToRequired: string;
      newRequirementPlaceholders: {
        [ProcessRequirementsSectionTypeDto.NiceToHave]: string;
        [ProcessRequirementsSectionTypeDto.Required]: string;
      };
      niceToHaveDescription: string;
      requiredDescription: string;
      requirement: string;
      saveChanges: string;
      subtitle: string;
      tabs: {
        [ProcessRequirementsSectionTypeDto.NiceToHave]: string;
        [ProcessRequirementsSectionTypeDto.Required]: string;
      };
      title: string;
    };
    title: {
      setProcessLanguages: string;
      setProcessRedFlags: string;
      setProcessRequirements: string;
    };
  };
  list: ProcessListTranslation;
};

export const processesTranslation: ProcessesTranslation = {
  createProcess: createProcessTranslation,
  details: processDetailsTranslation,
  form: {
    buttons: {
      back: 'Back',
      close: 'Close',
      next: 'Next',
      save: 'Save',
    },
    cvSearch: {
      cancel: 'Cancel',
      confirm: 'Confirm',
      cvSearchTitle: 'CV Search criteria',
      dialogTitle: 'CV Search',
      emptyTags: 'Add at least one keyword or skip CV Search.',
      failed: 'We could not prepare CV Search. Try again or confirm without it.',
      finishingAnalysis: 'Finishing analysis…',
      generatingHints:
        'Generating keyword and CV Search requirement hints from the job description…',
      locationRegionMatched:
        'The location "{{location}}" is not on the city list, so we set the region: {{region}}. Pick the city manually if needed.',
      locationRemote: 'The process is remote, so no location is set.',
      locationUnrecognized: 'We did not recognize the location "{{location}}". Set it manually.',
      retry: 'Try again',
      skip: 'Skip',
      started: 'The default CV Search is already running. Confirming only activates the process.',
      subtitle: 'Confirm the default CV Search criteria. The search runs in the background.',
      unrecognizedLanguages:
        'We did not recognize these languages: {{languages}}. Add them manually.',
    },
    languages: {
      addNewLanguage: 'Add language',
      cancel: 'Cancel',
      deleteLanguage: 'Delete language',
      dialogTitle: 'Process languages',
      infotip:
        'Every language is automatically included in AI analysis with the highest weight (W5).',
      language: 'Language',
      level: 'Level',
      newLanguagePlaceholder: 'Enter language name',
      saveChanges: 'Save changes',
      sectionTitle: 'Languages',
      subtitle: 'Required languages.',
      tabs: {
        [ProcessLanguageLevelDto.Advanced]: 'Advanced',
        [ProcessLanguageLevelDto.Basic]: 'Basic',
        [ProcessLanguageLevelDto.Intermediate]: 'Intermediate',
        [ProcessLanguageLevelDto.Native]: 'Native',
        [ProcessLanguageLevelDto.Proficient]: 'Proficient',
        [ProcessLanguageLevelDto.Unknown]: 'Unknown',
      },
      title: 'Languages',
    },
    redFlags: {
      addNewRedFlag: 'Add red flag',
      cancel: 'Cancel',
      deleteRedFlag: 'Delete red flag',
      dialogTitle: 'Process red flags',
      infotip:
        'List the red flags in the field below that should be considered when analyzing the requirements of the process.',
      newRedFlagsPlaceholder: 'write the red flag',
      redFlag: 'Red flag',
      saveChanges: 'Save changes',
      sectionTitle: 'Red flags',
      subtitle: 'Define warning signals that should affect candidate evaluation.',
      title: 'Red Flags',
    },
    requirements: {
      actions: 'Actions',
      addNew: 'Add new',
      addNiceToHave: 'Add nice to have requirement',
      addRequired: 'Add required requirement',
      cancel: 'Cancel',
      deleteRequirement: 'Delete requirement',
      dialogTitle: 'Process requirements',
      importance: 'Importance',
      importanceValue: 'Importance {{value}}',
      infotip:
        'Move the slider from 1 to 5 to assess how important the given issue is in the process.',
      moveToNiceToHave: 'Move to nice to have',
      moveToRequired: 'Move to required',
      newRequirementPlaceholders: {
        [ProcessRequirementsSectionTypeDto.NiceToHave]: 'write new, nice to have issue',
        [ProcessRequirementsSectionTypeDto.Required]: 'write new, required issue',
      },
      niceToHaveDescription: 'Additional skills that increase candidate fit.',
      requiredDescription: 'Candidates should meet these requirements.',
      requirement: 'Requirement',
      saveChanges: 'Save changes',
      subtitle: 'Define requirements and their importance in candidate evaluation.',
      tabs: {
        [ProcessRequirementsSectionTypeDto.NiceToHave]: 'Nice to have',
        [ProcessRequirementsSectionTypeDto.Required]: 'Required',
      },
      title: 'Requirements',
    },
    title: {
      setProcessLanguages: 'Set required languages',
      setProcessRedFlags: 'Set process red flags',
      setProcessRequirements: 'Set process requirements',
    },
  },
  list: processListTranslation,
};
